import React from 'react';
import * as HeroIcons from "@heroicons/react/24/outline";
import { useAuth } from '../../app/provider';
import { BreadcrumbProvider } from '../../app/breadcrumb-provider';
import { MenuPermission } from '../../features/auth/types';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
} from '../ui/sidebar';
import { NavMain } from './NavMain';
import { NavUser } from './NavUser';
import { PageHeader } from './PageHeader';

interface MenuItem {
  title: string;
  url: string;
  icon: any;
  isActive?: boolean;
  items?: {
    title: string;
    url: string;
  }[];
}

const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';

const getIconComponent = (iconName: string) => {
  return (HeroIcons as any)[iconName] || HeroIcons.HomeIcon;
};

const transformToNavMain = (permissions: MenuPermission[]): MenuItem[] => {
  console.log('transformToNavMain called with permissions:', permissions);

  if (!permissions || !Array.isArray(permissions)) {
    console.log('No permissions or not an array');
    return [];
  }

  const result = permissions
    .sort((a, b) => a.display_order - b.display_order)
    .map(item => {
      const parentRoutePath = item.route_path === '/' ? '/' : item.route_path;

      const subItems = item.submenu && item.submenu.length > 0
        ? item.submenu
          .sort((a, b) => a.display_order - b.display_order)
          .map(subItem => ({
            title: subItem.menu_desc.charAt(0).toUpperCase() + subItem.menu_desc.slice(1),
            url: subItem.route_path,
          }))
        : undefined;

      const isParentActive = currentPath === parentRoutePath ||
        (subItems?.some(sub => sub.url === currentPath) ?? false);

      return {
        title: item.menu_desc.charAt(0).toUpperCase() + item.menu_desc.slice(1),
        url: parentRoutePath,
        icon: getIconComponent(item.icon_name),
        isActive: isParentActive,
        items: subItems
      };
    });

  console.log('transformToNavMain result:', result);
  return result;
};

function AppSidebar() {
  const { user, permissions } = useAuth();

  // Memoize the nav items to prevent unnecessary re-renders
  const navItems = React.useMemo(() => {
    return transformToNavMain(permissions);
  }, [permissions]);

  const userData = React.useMemo(() => ({
    name: user?.full_name || 'User',
    email: user?.cust_name || '',
    avatar: '',
  }), [user]);

  return (
    <Sidebar collapsible="offcanvas">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <a href="/">
                <div className="grid flex-1 text-center text-sm leading-tight">
                  <img src="/logo.png" alt="MRC Logistics Logo" className="h-10 w-auto" />
                </div>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={navItems} />
      </SidebarContent>
      <SidebarFooter>
        <NavUser user={userData} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}

interface AppShellProps {
  children: React.ReactNode;
  minimal?: boolean;
}

export const AppShell: React.FC<AppShellProps> = ({ children, minimal = false }) => {
  if (minimal) {
    return (
      <BreadcrumbProvider>
        <main className="h-screen flex flex-col overflow-hidden">
          <div className="flex-1 min-h-0 p-4">
            {children}
          </div>
        </main>
      </BreadcrumbProvider>
    );
  }

  return (
    <BreadcrumbProvider>
      <SidebarProvider>
        <AppSidebar />
        <main className="w-full h-screen flex flex-col overflow-hidden">
          <div className="shrink-0">
            <PageHeader />
          </div>
          <div className="flex-1 min-h-0 w-full p-4">
            {children}
          </div>
        </main>
      </SidebarProvider>
    </BreadcrumbProvider>
  );
};
